import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { useCreateBoard, CreateBoardPersona } from "@/hooks/useCreateBoard";

interface FinalReviewStepProps {
  boardDetails: { name: string; description: string; };
  personas: any[];
  onSuccess: (boardId: string) => void;
}

const FinalReviewStep = ({ boardDetails, personas, onSuccess }: FinalReviewStepProps) => {
  const { createBoard, isCreating } = useCreateBoard();
  const [boardName, setBoardName] = useState(boardDetails.name);

  const handleCreateBoard = async () => {
    if (!boardName.trim()) {
      toast.error("Board name cannot be empty");
      return;
    }

    await createBoard({
      boardName,
      description: boardDetails.description,
      personas: personas as CreateBoardPersona[],
      boardType: "ai_generated",
      onSuccess
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-semibold">Review Your New Board</h1>
        <p className="text-muted-foreground">AI has generated the following board for you. Review and create it, or go back to make changes.</p>
      </div>
      <Card>
        <CardHeader>
          <Label htmlFor="boardName">Board Name</Label>
          <Input id="boardName" value={boardName} onChange={(e) => setBoardName(e.target.value)} />
          <CardDescription>{boardDetails.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <h3 className="font-semibold">Expert Personas ({personas.length})</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {personas.map((p, i) => (
              <Card key={i}>
                <CardHeader>
                  <CardTitle className="text-base">{p.role_name}</CardTitle>
                  <CardDescription>{p.mission}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
      <Button onClick={handleCreateBoard} disabled={isCreating} className="w-full">
        {isCreating ? "Creating Board..." : "Looks Good, Create Board"}
      </Button>
    </div>
  );
};

export default FinalReviewStep;
